import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Building2, CircleCheck, ClipboardList, Wallet, TriangleAlert, ArrowUpRight,
    Store, KeyRound, HardDriveDownload, MoonStar, ChevronRight, ImageIcon, ShieldCheck,
} from 'lucide-react';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import SuperadminLayout from '../../components/SuperadminLayout';

const money = (v) => `${Number(v || 0).toLocaleString('az-AZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₼`;

// Lisenziya vəziyyəti → diqqət siyahısı üçün etiket
const licenseIssue = (lic) => {
    if (!lic) return null;
    if (lic.ok) {
        if (lic.days_left != null && lic.days_left <= 7) return { tone: 'amber', text: lic.days_left <= 0 ? 'Bu gün bitir' : `${lic.days_left} gün qalıb` };
        return null;
    }
    const map = {
        license_expired: 'Lisenziya bitib',
        missing_dukan: 'Dükan kodu yoxdur',
        license_disabled: 'Lisenziya deaktiv',
        license_not_found: 'Lisenziya tapılmadı',
    };
    if (['timeout', 'fetch_error', 'license_fetch_failed'].includes(lic.reason)) return null; // yoxlanmayıb, problem deyil
    return { tone: 'red', text: map[lic.reason] || 'Lisenziya deaktiv' };
};

const TONE = {
    red:   'bg-red-50 text-red-700 ring-red-200',
    amber: 'bg-amber-50 text-amber-700 ring-amber-200',
    sky:   'bg-sky-50 text-sky-700 ring-sky-200',
};

const StatCard = ({ Icon, label, value, hint, accent, loading, children }) => (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <div className="flex items-start justify-between">
            <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${accent}`}>
                <Icon className="h-5 w-5" strokeWidth={2} />
            </div>
            {hint && <span className="text-[11px] font-medium text-slate-400">{hint}</span>}
        </div>
        <div className="mt-4 text-[28px] font-bold leading-none tracking-tight text-slate-900">
            {loading ? <span className="inline-block h-7 w-16 animate-pulse rounded-md bg-slate-100" /> : value}
        </div>
        <div className="mt-1.5 text-sm text-slate-500">{label}</div>
        {children}
    </div>
);

const SuperAdminDashboard = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tenants, setTenants] = useState([]);
    const [licenses, setLicenses] = useState({});
    const [attnLoading, setAttnLoading] = useState(true);

    useEffect(() => {
        api.get('/superadmin/stats')
            .then(res => setStats(res.data.data))
            .catch(() => toast.error('Statistika yüklənmədi.'))
            .finally(() => setLoading(false));
        // Diqqət bloku üçün: restoranlar + lisenziya statusları (səssiz — xəta olsa blok boş qalır)
        Promise.allSettled([api.get('/superadmin/tenants'), api.get('/superadmin/licenses')])
            .then(([t, l]) => {
                if (t.status === 'fulfilled') setTenants(t.value.data?.data || []);
                if (l.status === 'fulfilled') setLicenses(l.value.data?.data || {});
            })
            .finally(() => setAttnLoading(false));
    }, []);

    const rev = stats?.revenue;
    const activePct = stats?.total ? Math.round((Number(stats.active || 0) / Number(stats.total)) * 100) : 0;
    const proShare = rev && Number(rev.total) > 0 ? Math.round((Number(rev.pro_revenue || 0) / Number(rev.total)) * 100) : 0;

    // Diqqət tələb edən restoranlar: lisenziya problemi və ya təsdiq gözləyən giriş şəkli
    const attention = useMemo(() => {
        const list = [];
        tenants.filter((t) => t.active).forEach((t) => {
            const issue = licenseIssue(licenses[t.id]);
            if (issue) list.push({ id: `${t.id}-lic`, name: t.name, ...issue, Icon: TriangleAlert });
            if (t.login_image_pending_url) list.push({ id: `${t.id}-img`, name: t.name, tone: 'sky', text: 'Giriş şəkli təsdiq gözləyir', Icon: ImageIcon });
        });
        const order = { red: 0, amber: 1, sky: 2 };
        return list.sort((a, b) => order[a.tone] - order[b.tone]);
    }, [tenants, licenses]);

    const QUICK = [
        { Icon: Store, title: 'Restoranlar', desc: 'Yeni restoran, plan, şifrə, fiskal kassa', href: '/superadmin/tenants', accent: 'bg-sky-50 text-sky-600' },
        { Icon: KeyRound, title: 'Tenantlara giriş', desc: 'Restoranın panelinə admin kimi daxil ol', href: '/superadmin/impersonate', accent: 'bg-violet-50 text-violet-600' },
        { Icon: HardDriveDownload, title: 'Baza backup', desc: 'Bazanın nüsxəsini endir', href: '/superadmin/backup', accent: 'bg-emerald-50 text-emerald-600' },
        { Icon: MoonStar, title: 'Gecə backup-ları', desc: 'Avtomatik gecə nüsxələri', href: '/superadmin/nightly', accent: 'bg-indigo-50 text-indigo-600' },
    ];

    return (
        <SuperadminLayout title="Dashboard" subtitle="Sistemin ümumi vəziyyəti">
            {/* Göstəricilər */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <StatCard Icon={Building2} label="Ümumi restoran" value={stats?.total ?? 0} loading={loading} accent="bg-slate-100 text-slate-700" />
                <StatCard Icon={CircleCheck} label="Aktiv restoran" value={stats?.active ?? 0} hint={!loading && stats?.total ? `${activePct}%` : null} loading={loading} accent="bg-emerald-50 text-emerald-600">
                    {!loading && (
                        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100">
                            <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${activePct}%` }} />
                        </div>
                    )}
                </StatCard>
                <StatCard Icon={ClipboardList} label="Bu günkü sifarişlər" value={Number(stats?.orders_today ?? 0).toLocaleString('az-AZ')} hint="bu gün" loading={loading} accent="bg-amber-50 text-amber-600" />
                <StatCard Icon={Wallet} label="Aylıq gəlir" value={money(rev?.total)} hint="ay" loading={loading} accent="bg-sky-50 text-sky-600" />
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-5">
                {/* Gəlir bölgüsü */}
                <div className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)] lg:col-span-2">
                    <div className="flex items-center justify-between">
                        <h2 className="text-sm font-semibold text-slate-900">Gəlir bölgüsü</h2>
                        <span className="text-xs text-slate-400">aylıq, AZN</span>
                    </div>
                    {loading || !rev ? (
                        <div className="mt-6 space-y-3">
                            {[0, 1, 2].map((i) => <div key={i} className="h-5 animate-pulse rounded bg-slate-100" />)}
                        </div>
                    ) : (
                        <>
                            <div className="mt-4 text-3xl font-bold tracking-tight text-slate-900">{money(rev.total)}</div>
                            <div className="mt-4 flex h-2.5 overflow-hidden rounded-full bg-slate-100">
                                <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400" style={{ width: `${proShare}%` }} />
                                <div className="h-full bg-gradient-to-r from-sky-500 to-sky-400" style={{ width: `${100 - proShare}%` }} />
                            </div>
                            <div className="mt-5 space-y-3 text-sm">
                                <div className="flex items-center justify-between">
                                    <span className="flex items-center gap-2 text-slate-600">
                                        <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                                        Pro <span className="text-slate-400">· {rev.pro_units} × {money(rev.pro_price)}</span>
                                    </span>
                                    <span className="font-semibold text-slate-900">{money(rev.pro_revenue)}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="flex items-center gap-2 text-slate-600">
                                        <span className="h-2.5 w-2.5 rounded-full bg-sky-500" />
                                        Sadə <span className="text-slate-400">· {rev.basic_units} × {money(rev.basic_price)}</span>
                                    </span>
                                    <span className="font-semibold text-slate-900">{money(rev.basic_revenue)}</span>
                                </div>
                                <div className="flex items-center justify-between border-t border-slate-100 pt-3 text-slate-500">
                                    <span className="flex items-center gap-2">
                                        <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
                                        Deaktiv restoran
                                    </span>
                                    <span className="font-medium">{rev.inactive}</span>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Diqqət tələb edir */}
                <div className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)] lg:col-span-3">
                    <div className="flex items-center justify-between">
                        <h2 className="text-sm font-semibold text-slate-900">Diqqət tələb edir</h2>
                        {!attnLoading && attention.length > 0 && (
                            <button onClick={() => navigate('/superadmin/tenants?filter=attention')}
                                className="flex items-center gap-1 text-xs font-medium text-sky-600 hover:text-sky-700">
                                Hamısına bax <ChevronRight className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </div>
                    {attnLoading ? (
                        <div className="mt-5 space-y-2.5">
                            {[0, 1, 2].map((i) => <div key={i} className="h-11 animate-pulse rounded-xl bg-slate-100" />)}
                        </div>
                    ) : attention.length === 0 ? (
                        <div className="mt-5 flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 py-10 text-center">
                            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                                <ShieldCheck className="h-5 w-5" />
                            </div>
                            <div className="mt-3 text-sm font-medium text-slate-700">Hər şey qaydasındadır</div>
                            <div className="mt-0.5 text-xs text-slate-400">Lisenziya problemi və gözləyən təsdiq yoxdur.</div>
                        </div>
                    ) : (
                        <ul className="mt-4 divide-y divide-slate-100">
                            {attention.slice(0, 6).map((a) => (
                                <li key={a.id}>
                                    <button onClick={() => navigate('/superadmin/tenants?filter=attention')}
                                        className="flex w-full items-center gap-3 rounded-lg px-1 py-2.5 text-left transition-colors hover:bg-slate-50">
                                        <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ring-1 ${TONE[a.tone]}`}>
                                            <a.Icon className="h-4 w-4" />
                                        </span>
                                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{a.name}</span>
                                        <span className={`flex-shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ${TONE[a.tone]}`}>{a.text}</span>
                                    </button>
                                </li>
                            ))}
                            {attention.length > 6 && (
                                <li className="pt-2.5 text-xs text-slate-400">və daha {attention.length - 6} restoran</li>
                            )}
                        </ul>
                    )}
                </div>
            </div>

            {/* Sürətli keçidlər */}
            <h2 className="mb-3 mt-8 text-sm font-semibold text-slate-900">Sürətli keçidlər</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {QUICK.map((q) => (
                    <button key={q.href} onClick={() => navigate(q.href)}
                        className="group flex items-start gap-3 rounded-2xl border border-slate-200/70 bg-white p-4 text-left shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md">
                        <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${q.accent}`}>
                            <q.Icon className="h-5 w-5" />
                        </span>
                        <span className="min-w-0 flex-1">
                            <span className="flex items-center justify-between text-sm font-semibold text-slate-900">
                                {q.title}
                                <ArrowUpRight className="h-4 w-4 text-slate-300 transition-colors group-hover:text-slate-500" />
                            </span>
                            <span className="mt-0.5 block text-xs leading-relaxed text-slate-500">{q.desc}</span>
                        </span>
                    </button>
                ))}
            </div>
        </SuperadminLayout>
    );
};

export default SuperAdminDashboard;
